import React, { useState, useEffect, useMemo } from "react";
import { Box, Text, BlockStack, Button, InlineStack, Checkbox, Select, Divider, Icon } from "@shopify/polaris";
import { AlertCircleIcon } from "@shopify/polaris-icons";
import { DEFAULT_POPUP_BACKGROUND_OPACITY } from "../../settings/defaultFormState";
import { normalizePopupLayout } from "../../settings/popupLayout";
import {
  normalizePopupVerificationMethod,
  normalizeCheckoutVerificationMethod,
  parseDateOrderTokens,
  coerceDateFormatType,
  dateFormatSeparator,
  clampPopupVerifyAge,
  popupAcceptLabel,
  popupRejectLabel,
  popupCheckboxConfirmLabel,
  formatPopupTextWithAge,
} from "../../settings/verificationOptions";

function normalizePopupAnimation(v) {
  if (v === "fade") return "fade_in";
  if (v === "slide") return "slide_in_up";
  if (v === "zoom") return "zoom_in";
  return v || "none";
}

function popupAnimationClass(animation) {
  const v = normalizePopupAnimation(animation);
  if (!v || v === "none") return "";
  return `age-preview-anim-${v}`;
}

/** Editor-only hint: marks areas where a merchant image can appear. */
function PopupImagePlaceholderSvg({ size = 72, dark = false }) {
  const stroke = dark ? "rgba(250,250,249,0.5)" : "rgba(75,85,99,0.45)";
  const fill = dark ? "rgba(250,250,249,0.1)" : "rgba(75,85,99,0.07)";
  const accent = dark ? "rgba(250,250,249,0.35)" : "rgba(75,85,99,0.35)";
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 56 56"
      aria-hidden
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <rect x="6" y="10" width="44" height="36" rx="5" fill={fill} stroke={stroke} strokeWidth="1.75" />
      <circle cx="20" cy="24" r="4.5" fill={accent} />
      <path d="M8 42 L22 28 L34 36 L48 20 V42 H8 Z" fill={accent} opacity={0.55} />
    </svg>
  );
}

/** Checkout preview: error copy when DOB invalid / under age (same template fields as live checkout UI). */
function checkoutBlockBuyerBannerText(checkout, minAge) {
  const dobErr = String(checkout?.dob?.errorMessage || "").trim();
  if (dobErr) return formatPopupTextWithAge(dobErr, minAge);
  const instr = String(checkout?.instructionMessage || "").trim();
  if (instr) return formatPopupTextWithAge(instr, minAge);
  return "";
}

const CHECKOUT_PREVIEW_ERROR_SURFACE = {
  background: "#fef2f2",
  borderColor: "#f87171",
};

/** Popup "background opacity" must not use CSS `opacity` on the whole modal (it fades the image column too and shows the outer backdrop through). */
/** Same rules as `ageshield-storefront.js` — admin may store 0–1 or 0–100. */
function normalizeOverlayAlpha01(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n)) return 0.6;
  if (n > 0 && n <= 1) return Math.min(1, Math.max(0, n));
  return Math.min(1, Math.max(0, n / 100));
}

function normalizePanelAlpha01(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n)) return 1;
  if (n > 0 && n <= 1) return Math.min(1, Math.max(0, n));
  return Math.min(1, Math.max(0, n / 100));
}

/** If stored color is a dark hex, use a light fallback so Template 3 stays readable with legacy defaults. */
function readableOnDarkBg(raw, fallbackLight) {
  const s = String(raw ?? "").trim();
  if (!s) return fallbackLight;
  const m = /^#([0-9a-f]{6})$/i.exec(s);
  if (!m) return s;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return lum < 0.45 ? fallbackLight : s;
}

/** Light templates: if stored color is very light (e.g. left over from Template 3), use dark default. */
function readableOnLightBg(raw, fallbackDark) {
  const s = String(raw ?? "").trim();
  if (!s) return fallbackDark;
  const m = /^#([0-9a-f]{6})$/i.exec(s);
  if (!m) return s;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return lum > 0.82 ? fallbackDark : s;
}

function confirmCheckboxLabelStyle(cb, isDarkPanel) {
  const fontFamily = String(cb?.font ?? "").trim() || "'Inter', sans-serif";
  const fontWeight = Number(cb?.weight) || 400;
  const fontSize = String(cb?.size ?? "").trim() || "15px";
  const raw = cb?.color;
  const color = isDarkPanel
    ? readableOnDarkBg(raw, "#fafafa")
    : readableOnLightBg(raw, "#111827");
  return { fontFamily, fontWeight, fontSize, color, lineHeight: 1.4 };
}

function hexToRgba(hex, alpha0to1) {
  const a = Math.min(1, Math.max(0, alpha0to1));
  let h = String(hex || "#ffffff").trim();
  if (!h.startsWith("#")) return `rgba(255,255,255,${a})`;
  h = h.slice(1);
  if (h.length === 3) {
    h = `${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`;
  }
  if (h.length !== 6 || !/^[0-9a-fA-F]+$/.test(h)) {
    return `rgba(255,255,255,${a})`;
  }
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}

/**
 * Live preview for Popup (modal) vs Checkout — popup layout 1–4 have distinct compositions.
 */
export default function AgeGatePreview({ mode, config, checkoutShowValidationError = false }) {
  const p = config?.popup || {};
  const c = config?.checkout || {};

  const yearDobOptions = useMemo(
    () =>
      Array.from({ length: 85 }, (_, i) => {
        const y = new Date().getFullYear() - 10 - i;
        return { label: String(y), value: String(y) };
      }),
    [],
  );
  const monthDobOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => {
        const m = String(i + 1).padStart(2, "0");
        return { label: m, value: m };
      }),
    [],
  );
  const dayDobOptions = useMemo(
    () =>
      Array.from({ length: 31 }, (_, i) => {
        const d = String(i + 1).padStart(2, "0");
        return { label: d, value: d };
      }),
    [],
  );

  const [checkoutDobDay, setCheckoutDobDay] = useState("15");
  const [checkoutDobMonth, setCheckoutDobMonth] = useState("01");
  const [checkoutDobYear, setCheckoutDobYear] = useState(() => String(new Date().getFullYear() - 25));
  const [checkoutCbChecked, setCheckoutCbChecked] = useState(false);
  const [popupDobDay, setPopupDobDay] = useState("");
  const [popupDobMonth, setPopupDobMonth] = useState("");
  const [popupDobYear, setPopupDobYear] = useState("");
  const [popupCbChecked, setPopupCbChecked] = useState(false);

  useEffect(() => {
    if (mode !== "checkout") return;
    const chMethod = normalizeCheckoutVerificationMethod(config?.checkout?.verificationMethod);
    if (chMethod !== "dob") return;
    if (checkoutShowValidationError) {
      setCheckoutDobDay("");
      setCheckoutDobMonth("");
      setCheckoutDobYear("");
    } else {
      setCheckoutDobDay("15");
      setCheckoutDobMonth("01");
      setCheckoutDobYear(String(new Date().getFullYear() - 25));
    }
  }, [mode, checkoutShowValidationError, config?.checkout?.verificationMethod]);

  useEffect(() => {
    if (mode !== "popup") return;
    setPopupDobDay("");
    setPopupDobMonth("");
    setPopupDobYear("");
    setPopupCbChecked(false);
  }, [mode, p.dob?.dateFormat, p.dob?.dateOrder, p.verificationMethod]);

  const style = p.popupStyle || {};
  const text = p.text || {};
  const btn = p.buttons || {};
  const titlePlain = String(text.title?.text ?? "").trim();
  const descPlain = String(text.description?.text ?? "").trim();

  const outerOpacity = normalizeOverlayAlpha01(style.outerOpacity);
  const bgOpacity = normalizePanelAlpha01(
    style.backgroundOpacity != null ? style.backgroundOpacity : DEFAULT_POPUP_BACKGROUND_OPACITY,
  );
  const heightRaw = style.height;
  const heightVal =
    heightRaw == null || heightRaw === "auto"
      ? "auto"
      : typeof heightRaw === "number"
        ? `${heightRaw}px`
        : `${parseInt(String(heightRaw), 10) || 400}px`;

  /* Store backdrop: image XOR color (same as storefront). Opacity applies to whichever layer is active. */
  const outerTint = hexToRgba(style.outerColor || "#000000", outerOpacity);
  function outerBackdropImageSwitchOn(enabled) {
    return (
      enabled === true ||
      enabled === 1 ||
      String(enabled).toLowerCase() === "true" ||
      String(enabled) === "1"
    );
  }
  function outerBackdropImageOn(enabled, url) {
    return outerBackdropImageSwitchOn(enabled) && String(url || "").trim().length > 0;
  }
  const useOuterImage = outerBackdropImageOn(style.outerImageEnabled, style.outerImageUrl);
  const showOuterImageSlotHint =
    outerBackdropImageSwitchOn(style.outerImageEnabled) && String(style.outerImageUrl || "").trim().length === 0;

  if (mode === "checkout") {
    const chMethod = normalizeCheckoutVerificationMethod(c.verificationMethod);
    const minAge = c.ageRule?.minAge ?? 18;
    const instructionRaw = String(c.instructionMessage ?? "").trim();
    const verificationMessageText = instructionRaw
      ? formatPopupTextWithAge(instructionRaw, minAge)
      : "";
    const checkoutDobFmt = coerceDateFormatType(c.dob?.dateFormat);
    const dobOrder = parseDateOrderTokens(c.dob?.dateOrder, checkoutDobFmt);
    const checkoutDobSep = dateFormatSeparator(checkoutDobFmt);
    const showBlockedPreview =
      !!checkoutShowValidationError && (chMethod === "dob" || chMethod === "checkbox");
    const topBannerText = checkoutBlockBuyerBannerText(c, minAge);

    const dobField = (token) => {
      const t = String(token).toUpperCase();
      const dobErr = showBlockedPreview && chMethod === "dob";
      if (t === "YY" || t === "YYYY")
        return (
          <div key="yy" style={{ flex: "1 1 120px", minWidth: 0 }}>
            <Select
              label="Year"
              options={yearDobOptions}
              value={checkoutDobYear}
              placeholder="YYYY"
              error={dobErr && !checkoutDobYear}
              onChange={setCheckoutDobYear}
            />
          </div>
        );
      if (t === "MM")
        return (
          <div key="mm" style={{ flex: "1 1 100px", minWidth: 0 }}>
            <Select
              label="Month"
              options={monthDobOptions}
              value={checkoutDobMonth}
              placeholder="MM"
              error={dobErr && !checkoutDobMonth}
              onChange={setCheckoutDobMonth}
            />
          </div>
        );
      if (t === "DD")
        return (
          <div key="dd" style={{ flex: "1 1 100px", minWidth: 0 }}>
            <Select
              label="Day"
              options={dayDobOptions}
              value={checkoutDobDay}
              placeholder="DD"
              error={dobErr && !checkoutDobDay}
              onChange={setCheckoutDobDay}
            />
          </div>
        );
      return null;
    };

    const ageBlockShell = showBlockedPreview
      ? {
          padding: "16px 16px 18px",
          borderRadius: 8,
          border: `1px solid ${CHECKOUT_PREVIEW_ERROR_SURFACE.borderColor}`,
          background: CHECKOUT_PREVIEW_ERROR_SURFACE.background,
          boxShadow: "none",
        }
      : {
          padding: "16px 16px 18px",
          borderRadius: 8,
          border: "1px solid #c9cccf",
          background: "#ffffff",
          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.06)",
        };

    return (
      <Box padding="300" maxWidth="640px" marginInline="auto">
        <BlockStack gap="300">
          <Text as="p" variant="bodySm" tone="subdued">
            Checkout preview · Min age {minAge}
            {!config?.checkoutEnabled ? " · Checkout verification is off for this template" : ""}
          </Text>
          <div
            style={{
              background: "#fff",
              borderRadius: 8,
              border: "1px solid #d2d5d8",
              boxShadow: "0 1px 0 rgba(0,0,0,0.05)",
              overflow: "hidden",
            }}
          >
            {showBlockedPreview ? (
              <div
                role="status"
                style={{
                  display: "flex",
                  gap: "10px",
                  alignItems: "flex-start",
                  padding: "12px 16px",
                  background: CHECKOUT_PREVIEW_ERROR_SURFACE.background,
                  borderBottom: `1px solid ${CHECKOUT_PREVIEW_ERROR_SURFACE.borderColor}`,
                }}
              >
                <div style={{ flexShrink: 0, marginTop: "1px", color: "#d72c0d" }}>
                  <Icon source={AlertCircleIcon} tone="critical" />
                </div>
                {topBannerText.trim() ? (
                  <Text as="p" variant="bodyMd" tone="critical">
                    {topBannerText}
                  </Text>
                ) : null}
              </div>
            ) : null}
            <div
              style={{
                padding: "16px 20px",
                background: "#fafafa",
                borderBottom: "1px solid #e1e3e5",
                opacity: 0.5,
                filter: "grayscale(0.06)",
              }}
            >
              <Text as="p" variant="bodySm" tone="subdued">
                Information
              </Text>
            </div>
            <div style={{ padding: "20px 24px" }}>
              <BlockStack gap="500">
                <div style={{ opacity: 0.52, filter: "grayscale(0.08)", pointerEvents: "none" }}>
                  <BlockStack gap="200">
                    <Text as="h3" variant="headingSm">
                      Contact
                    </Text>
                    <div
                      style={{
                        height: 44,
                        borderRadius: 6,
                        border: "1px solid #8c9196",
                        background: "#f1f2f4",
                      }}
                      aria-hidden
                    />
                    <Checkbox label="Email me with news and offers" checked={false} onChange={() => {}} />
                  </BlockStack>
                </div>

                <Divider />

                <>
                  <div style={ageBlockShell}>
                    <BlockStack gap="300">
                      {showBlockedPreview ? (
                        <InlineStack gap="200" blockAlign="center" wrap={false}>
                          <div style={{ color: "#d72c0d", display: "flex", flexShrink: 0 }}>
                            <Icon source={AlertCircleIcon} tone="critical" />
                          </div>
                          <Text as="h3" variant="headingSm">
                            Age verification
                          </Text>
                        </InlineStack>
                      ) : (
                        <Text as="h3" variant="headingSm">
                          Age Verification
                        </Text>
                      )}
                      <Text as="p" variant="bodyMd">
                        {verificationMessageText}
                      </Text>

                      {chMethod === "dob" ? (
                        <InlineStack gap="200" wrap blockAlign="center">
                          {dobOrder.flatMap((tok, idx) => {
                            const field = dobField(tok);
                            if (!field) return [];
                            if (idx === dobOrder.length - 1) return [field];
                            return [
                              field,
                              <Text key={`checkout-dob-sep-${idx}`} as="span" variant="bodySm" tone="subdued">
                                {checkoutDobSep}
                              </Text>,
                            ];
                          })}
                        </InlineStack>
                      ) : null}

                      {chMethod === "checkbox" ? (
                        <Checkbox
                          label={
                            <span style={confirmCheckboxLabelStyle(c.confirmCheckbox, false)}>
                              {popupCheckboxConfirmLabel(c.confirmCheckbox?.label, minAge)}
                            </span>
                          }
                          checked={checkoutCbChecked}
                          onChange={setCheckoutCbChecked}
                        />
                      ) : null}
                    </BlockStack>
                  </div>
                  <Divider />
                </>

                <div style={{ opacity: 0.52, filter: "grayscale(0.08)", pointerEvents: "none" }}>
                  <BlockStack gap="200">
                    <Text as="h3" variant="headingSm">
                      Delivery
                    </Text>
                    <div
                      style={{
                        height: 40,
                        borderRadius: 6,
                        border: "1px dashed #c9cccf",
                        background: "#ececed",
                      }}
                      aria-hidden
                    />
                  </BlockStack>
                </div>
            </BlockStack>
            </div>
          </div>
        </BlockStack>
      </Box>
    );
  }

  const layoutId = normalizePopupLayout(p.layout);
  const br = Number(style.borderRadius) || 16;
  const maxW = Math.min(Number(style.width) || 600, 720);
  const py = style.paddingY ?? 24;
  const px = style.paddingX ?? 24;

  /** Inner image: respect `backgroundImageEnabled` like storefront (URL alone when flag omitted). */
  const previewInnerBgUrl = String(style.backgroundImageUrl || "").trim();
  const innerBgFlagOff =
    style.backgroundImageEnabled === false ||
    style.backgroundImageEnabled === 0 ||
    String(style.backgroundImageEnabled).toLowerCase() === "false";
  const showInnerBackgroundPreview = previewInnerBgUrl.length > 0 && !innerBgFlagOff;
  const panelBackgroundRgba = hexToRgba(style.backgroundColor || "#ffffff", bgOpacity);

  const popupMinAge = clampPopupVerifyAge(p.verifyAge);
  const acceptBase = {
    padding: "12px 20px",
    borderRadius: btn.accept?.borderRadius ?? 10,
    border: `${btn.accept?.borderWidth ?? 1}px solid ${btn.accept?.borderColor || "#111"}`,
    background: btn.accept?.background || "#111",
    color: btn.accept?.color || "#fff",
    fontFamily: btn.accept?.font || "inherit",
    fontWeight: Number(btn.accept?.weight) || 600,
    fontSize: btn.accept?.size || "15px",
    cursor: "pointer",
    boxSizing: "border-box",
  };

  const rejectBase = {
    padding: "12px 20px",
    borderRadius: btn.reject?.borderRadius ?? 10,
    border: `${btn.reject?.borderWidth ?? 1}px solid ${btn.reject?.borderColor || "#e5e7eb"}`,
    background: btn.reject?.background || "#fff",
    color: btn.reject?.color || "#111",
    fontFamily: btn.reject?.font || "inherit",
    fontWeight: Number(btn.reject?.weight) || 600,
    fontSize: btn.reject?.size || "15px",
    cursor: "pointer",
    boxSizing: "border-box",
  };

  /** Template 3: avoid translucent reject chips; widen row so “No, I am under 18” stays one line. */
  function ghostRejectBgForDarkTemplate(bg) {
    const s = String(bg ?? "")
      .trim()
      .toLowerCase()
      .replace(/\s/g, "");
    if (!s || s === "transparent") return true;
    if (s.startsWith("rgba(255,255,255,")) return true;
    return false;
  }
  let rejectResolved = rejectBase;
  if (layoutId === "3" && ghostRejectBgForDarkTemplate(btn.reject?.background)) {
    rejectResolved = {
      ...rejectBase,
      background: "#44403c",
      color: "#fafafa",
      border: `${btn.reject?.borderWidth ?? 1}px solid #57534e`,
    };
  }

  const titleStyle = (lightFallback) => ({
    fontFamily: text.title?.font || "system-ui, -apple-system, Segoe UI, sans-serif",
    fontWeight: text.title?.weight || 700,
    fontSize: text.title?.size || "35px",
    color: lightFallback
      ? readableOnDarkBg(text.title?.color, "#fafafa")
      : readableOnLightBg(text.title?.color, "#111827"),
    lineHeight: 1.2,
    margin: 0,
  });

  const descStyle = (lightFallback) => ({
    fontFamily: text.description?.font || "system-ui, -apple-system, Segoe UI, sans-serif",
    fontWeight: text.description?.weight || 400,
    fontSize: text.description?.size || "15px",
    color: lightFallback
      ? readableOnDarkBg(text.description?.color, "#d6d3d1")
      : readableOnLightBg(text.description?.color, "#4b5563"),
    lineHeight: 1.5,
    margin: 0,
    maxWidth: layoutId === "1" ? 280 : layoutId === "4" ? 400 : 320,
  });

  function Logo({ ring }) {
    if (!p.logo?.show) return null;
    const side = p.logo?.square ? 52 : 64;
    const inner = p.logo?.url ? (
      <img
        src={p.logo.url}
        alt=""
        style={{
          width: side,
          height: side,
          objectFit: "contain",
          borderRadius: p.logo?.square ? 10 : "50%",
          display: "block",
        }}
      />
    ) : (
      <div
        style={{
          width: side,
          height: side,
          background: ring ? "#fff" : "#f3f4f6",
          borderRadius: p.logo?.square ? 10 : "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        aria-hidden
      >
        <PopupImagePlaceholderSvg size={Math.max(22, Math.round(side * 0.48))} dark={false} />
      </div>
    );
    if (!ring) return inner;
    return (
      <div
        style={{
          padding: 6,
          borderRadius: "50%",
          background: "#fff",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        }}
      >
        {inner}
      </div>
    );
  }

  function VerificationBlock({ light }) {
    const m = normalizePopupVerificationMethod(p.verificationMethod);

    if (m === "birthdate") {
      const popupDobFmt = coerceDateFormatType(p.dob?.dateFormat);
      const tokens = parseDateOrderTokens(p.dob?.dateOrder, popupDobFmt);
      const sep = dateFormatSeparator(popupDobFmt);
      const dobField = (tok) => {
        const t = String(tok).toUpperCase();
        if (t === "YY" || t === "YYYY")
          return (
            <div key="yy" style={{ flex: "1 1 100px", minWidth: 0, maxWidth: 120 }}>
              <Select
                label="Year"
                options={yearDobOptions}
                value={popupDobYear}
                placeholder="YYYY"
                onChange={setPopupDobYear}
              />
            </div>
          );
        if (t === "MM")
          return (
            <div key="mm" style={{ flex: "1 1 88px", minWidth: 0, maxWidth: 100 }}>
              <Select label="Month" options={monthDobOptions} value={popupDobMonth} placeholder="MM" onChange={setPopupDobMonth} />
            </div>
          );
        if (t === "DD")
          return (
            <div key="dd" style={{ flex: "1 1 88px", minWidth: 0, maxWidth: 100 }}>
              <Select label="Day" options={dayDobOptions} value={popupDobDay} placeholder="DD" onChange={setPopupDobDay} />
            </div>
          );
        return null;
      };
      return (
        <div style={{ width: "100%", maxWidth: 380 }}>
          <InlineStack gap="200" wrap blockAlign="center">
            {tokens.flatMap((tok, i) => {
              const field = dobField(tok);
              if (!field) return [];
              if (i === tokens.length - 1) return [field];
              return [
                field,
                <Text key={`popup-dob-sep-${i}`} as="span" variant="bodySm" tone="subdued">
                  {sep}
                </Text>,
              ];
            })}
          </InlineStack>
        </div>
      );
    }

    if (m === "checkbox") {
      const label = popupCheckboxConfirmLabel(p.confirmCheckbox?.label, popupMinAge);
      const cbTypography = confirmCheckboxLabelStyle(p.confirmCheckbox, layoutId === "3");
      return (
        <div style={{ display: "flex", justifyContent: "center", width: "100%", maxWidth: 400 }}>
          <Checkbox
            label={<span style={cbTypography}>{label}</span>}
            checked={popupCbChecked}
            onChange={setPopupCbChecked}
          />
        </div>
      );
    }

    return null;
  }

  function Privacy({ light }) {
    if (!p.privacy?.enabled) return null;
    const raw = String(p.privacy?.html || "").trim();
    if (!raw) return null;
    const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(raw);
    const color = light ? "rgba(255,255,255,0.75)" : "#6b7280";
    const baseStyle = {
      fontSize: 12,
      color,
      cursor: "default",
      textAlign: "center",
      maxWidth: 320,
    };
    if (looksLikeHtml) {
      return (
        <div
          className="age-preview-privacy-html"
          style={baseStyle}
          // Same model as storefront `innerHTML` — content comes from merchant admin only.
          dangerouslySetInnerHTML={{ __html: raw }}
        />
      );
    }
    return (
      <span
        style={{
          ...baseStyle,
          textDecoration: "underline",
          whiteSpace: "pre-wrap",
        }}
      >
        {raw}
      </span>
    );
  }

  function ButtonsStack() {
    const acceptLbl = popupAcceptLabel(btn.accept?.text, popupMinAge);
    const rejectLbl = popupRejectLabel(btn.reject?.text, popupMinAge);
    if (!acceptLbl && !rejectLbl) return null;
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          width: "100%",
          maxWidth: 280,
        }}
      >
        {acceptLbl ? (
          <button type="button" style={{ ...acceptBase, width: "100%" }} onClick={() => {}}>
            {acceptLbl}
          </button>
        ) : null}
        {rejectLbl ? (
          <button type="button" style={{ ...rejectBase, width: "100%" }} onClick={() => {}}>
            {rejectLbl}
          </button>
        ) : null}
      </div>
    );
  }

  function ButtonsRow({ maxWidth = 340 } = {}) {
    const acceptLbl = popupAcceptLabel(btn.accept?.text, popupMinAge);
    const rejectLbl = popupRejectLabel(btn.reject?.text, popupMinAge);
    if (!acceptLbl && !rejectLbl) return null;
    const rowMax = layoutId === "3" ? Math.max(maxWidth, 480) : maxWidth;
    /** Template 3: hug label width; horizontal padding slightly wider than minimum so buttons don’t feel too tight. */
    const t3Pad = { padding: "12px 16px" };
    const acceptFlex =
      layoutId === "3"
        ? {
            flex: "0 1 auto",
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 46,
            maxWidth: "100%",
          }
        : { flex: 1, minWidth: 0, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 46 };
    const rejectFlex =
      layoutId === "3"
        ? {
            flex: "0 1 auto",
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 46,
            maxWidth: "100%",
          }
        : { flex: 1, minWidth: 0, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 46 };
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 12,
          width: "100%",
          maxWidth: rowMax,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {acceptLbl ? (
          <button
            type="button"
            style={{ ...acceptBase, ...acceptFlex, ...(layoutId === "3" ? t3Pad : {}) }}
            onClick={() => {}}
          >
            {acceptLbl}
          </button>
        ) : null}
        {rejectLbl ? (
          <button
            type="button"
            style={{ ...rejectResolved, ...rejectFlex, ...(layoutId === "3" ? t3Pad : {}) }}
            onClick={() => {}}
          >
            {rejectLbl}
          </button>
        ) : null}
      </div>
    );
  }

  const modalShell = {
    width: "100%",
    maxWidth: maxW,
    minHeight: heightVal === "auto" ? undefined : heightVal,
    borderRadius: br,
    border: `${style.borderWidth ?? 0}px solid ${style.borderColor || "#e5e7eb"}`,
    boxShadow: "0 25px 50px -12px rgba(0,0,0,0.35), 0 0 0 1px rgba(0,0,0,0.06)",
    overflow: "hidden",
    /* Opaque shell matches storefront so outer backdrop does not bleed through gaps. */
    backgroundColor: layoutId === "3" ? "#1c1917" : "#ffffff",
    position: "relative",
    zIndex: 2,
    isolation: "isolate",
  };
  const animationClass = popupAnimationClass(style.animation);
  const animationKey = `${animationClass}-${layoutId}-${style.width ?? ""}-${style.height ?? ""}`;

  let inner = null;

  if (layoutId === "1") {
    inner = (
      <div style={{ display: "flex", flexDirection: "row", minHeight: heightVal === "auto" ? 340 : undefined }}>
        <div
          style={{
            width: "42%",
            minWidth: 120,
            flexShrink: 0,
            alignSelf: "stretch",
            position: "relative",
            overflow: "hidden",
            background: "#e8e6e4",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              opacity: bgOpacity,
            }}
          >
            {showInnerBackgroundPreview ? (
              <img
                src={previewInnerBgUrl}
                alt=""
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center",
                  display: "block",
                }}
              />
            ) : (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: "linear-gradient(165deg, #ebe6e0 0%, #cfc8bf 42%, #a8a29e 100%)",
                }}
              />
            )}
          </div>
          {!showInnerBackgroundPreview ? (
            <div
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
              }}
              aria-hidden
            >
              <PopupImagePlaceholderSvg size={92} dark={false} />
            </div>
          ) : null}
        </div>
        <div
              style={{
            flex: 1,
            padding: `${py}px ${px}px`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            gap: 14,
            backgroundColor: panelBackgroundRgba,
          }}
        >
          <Logo ring={false} />
          {titlePlain ? (
            <h2 style={titleStyle(false)}>{formatPopupTextWithAge(titlePlain, popupMinAge)}</h2>
          ) : null}
          {descPlain ? (
            <p style={descStyle(false)}>{formatPopupTextWithAge(descPlain, popupMinAge)}</p>
          ) : null}
          <VerificationBlock light={false} />
          <ButtonsStack />
          <Privacy light={false} />
        </div>
      </div>
    );
  } else if (layoutId === "2") {
    inner = (
      <div style={{ display: "flex", flexDirection: "column", background: "transparent" }}>
        <div
              style={{
            height: 148,
            flexShrink: 0,
            position: "relative",
            overflow: "hidden",
            background: "#e7e5e4",
          }}
        >
          <div style={{ position: "absolute", inset: 0, opacity: bgOpacity }}>
            {showInnerBackgroundPreview ? (
              <img
                src={previewInnerBgUrl}
                alt=""
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center top",
                  display: "block",
                }}
              />
            ) : (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: "linear-gradient(180deg, #f3eee6 0%, #e0d8cc 55%, #cfc6b8 100%)",
                }}
              />
            )}
          </div>
          {!showInnerBackgroundPreview ? (
            <div
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
              }}
              aria-hidden
            >
              <PopupImagePlaceholderSvg size={80} dark={false} />
            </div>
          ) : null}
        </div>
        <div
          style={{
            marginTop: -40,
            padding: `8px ${px}px ${py}px`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: 12,
            position: "relative",
            zIndex: 1,
            backgroundColor: panelBackgroundRgba,
          }}
        >
          <Logo ring />
          {titlePlain ? (
            <h2 style={{ ...titleStyle(false), marginTop: 4 }}>{formatPopupTextWithAge(titlePlain, popupMinAge)}</h2>
          ) : null}
          {descPlain ? (
            <p style={descStyle(false)}>{formatPopupTextWithAge(descPlain, popupMinAge)}</p>
          ) : null}
          <VerificationBlock light={false} />
          <ButtonsRow />
          <Privacy light={false} />
        </div>
      </div>
    );
  } else if (layoutId === "4") {
    inner = (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: 16,
          padding: `${py}px ${px}px`,
          backgroundColor: panelBackgroundRgba,
          minHeight: heightVal === "auto" ? undefined : heightVal,
          boxSizing: "border-box",
        }}
      >
        <Logo ring={false} />
        {titlePlain ? (
          <h2 style={titleStyle(false)}>{formatPopupTextWithAge(titlePlain, popupMinAge)}</h2>
        ) : null}
        {descPlain ? (
          <p style={descStyle(false)}>{formatPopupTextWithAge(descPlain, popupMinAge)}</p>
        ) : null}
        <VerificationBlock light={false} />
        <ButtonsRow maxWidth={480} />
        <Privacy light={false} />
      </div>
    );
  } else {
    /* Template 3 — dark premium */
    const t3TopPad = Number(py) + 20;
    /** With inner image: no scrim on top. Without image: default dark (black/stone) fill; inner opacity slider still applies. */
    const darkPanelBase = {
      position: "relative",
      minHeight: heightVal === "auto" ? 360 : undefined,
      padding: `${t3TopPad}px ${px}px ${py}px ${px}px`,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      gap: 14,
      overflow: "hidden",
      /* Fill comes from the absolute layer (image or inner background color). */
      backgroundColor: "transparent",
    };

    inner = (
      <div style={darkPanelBase}>
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            opacity: bgOpacity,
          }}
        >
          {showInnerBackgroundPreview ? (
            <img
              src={previewInnerBgUrl}
              alt=""
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center bottom",
                display: "block",
              }}
            />
          ) : (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "linear-gradient(180deg, #2a2624 0%, #1c1917 45%, #0f0e0d 100%)",
              }}
            />
          )}
        </div>
        {!showInnerBackgroundPreview ? (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
            aria-hidden
          >
            <PopupImagePlaceholderSvg size={96} dark />
          </div>
        ) : null}
        <svg
          viewBox="0 0 480 56"
          preserveAspectRatio="none"
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            height: 56,
            zIndex: 2,
            pointerEvents: "none",
          }}
          aria-hidden
        >
          <path
            fill="rgba(0,0,0,0.22)"
            d="M0,32 Q120,8 240,28 T480,24 L480,56 L0,56 Z"
          />
        </svg>
        <div
          style={{
            position: "relative",
            zIndex: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 14,
            width: "100%",
          }}
        >
          <Logo ring />
          {titlePlain ? (
            <h2 style={titleStyle(true)}>{formatPopupTextWithAge(titlePlain, popupMinAge)}</h2>
          ) : null}
          {descPlain ? (
            <p style={descStyle(true)}>{formatPopupTextWithAge(descPlain, popupMinAge)}</p>
          ) : null}
          <VerificationBlock light />
          <ButtonsRow />
          <Privacy light />
        </div>
      </div>
    );
  }

  return (
    <Box position="relative" minHeight="540px">
      <div
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: 12,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 16,
          overflow: "hidden",
        }}
      >
        {useOuterImage ? (
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: 12,
              zIndex: 0,
              backgroundImage: `url(${JSON.stringify(String(style.outerImageUrl || "").trim())})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              opacity: outerOpacity,
            }}
          />
        ) : (
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: 12,
              zIndex: 0,
              background: outerTint,
            }}
          />
        )}
        {showOuterImageSlotHint ? (
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: 12,
              zIndex: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <PopupImagePlaceholderSvg size={112} dark />
          </div>
        ) : null}
        <div
          key={animationKey}
          className={animationClass || undefined}
          style={{ ...modalShell, position: "relative", zIndex: 1 }}
        >
          {inner}
        </div>
      </div>
    </Box>
  );
}
